<template>
  <div class="batch-export-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>批量订单数据CSV导出工具</span>
      </div>
      
      <div class="content">
        <el-alert
          title="批量导出说明"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 20px;">
          <div>
            <p>本工具将根据预设的年度数据配置，批量生成并导出CSV文件：</p>
            <ul>
              <li>支持选择单个或多个年份批量导出</li>
              <li>每年的所有月份数据合并为一个CSV文件（如：order_data_2022.csv）</li>
              <li>自动获取课程类目，拼接成订单标题</li>
              <li>显示导出进度和统计信息</li>
              <li>日期格式：yyyy-MM-dd HH:mm:ss</li>
              <li>订单按时间升序排列</li>
            </ul>
          </div>
        </el-alert>

        <!-- 年份选择 -->
        <el-form label-width="120px" class="config-form">
          <el-form-item label="选择年份">
            <el-checkbox-group v-model="selectedYears" @change="handleYearChange">
              <el-checkbox 
                v-for="year in availableYears" 
                :key="year" 
                :label="year"
                :disabled="exporting">
                {{ year }}年
                <span v-if="yearStats[year]" class="year-stats">
                  ({{ yearStats[year].totalCourseOrders }}单)
                </span>
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>

          <el-form-item label="快速选择">
            <el-button-group>
              <el-button 
                size="small" 
                @click="selectAllYears"
                :disabled="exporting">
                全选
              </el-button>
              <el-button 
                size="small" 
                @click="deselectAllYears"
                :disabled="exporting">
                清空
              </el-button>
              <el-button 
                size="small" 
                @click="selectRecentYears"
                :disabled="exporting">
                最近2年
              </el-button>
            </el-button-group>
          </el-form-item>

          <!-- 年度统计 -->
          <el-form-item label="年度统计" v-if="selectedYears.length > 0">
            <el-table :data="selectedYearStats" border style="width: 100%;">
              <el-table-column prop="year" label="年份" width="100"></el-table-column>
              <el-table-column prop="totalCourseOrders" label="课程订单" width="120"></el-table-column>
              <el-table-column prop="totalCoachOrders" label="教练订单" width="120"></el-table-column>
              <el-table-column prop="totalStoreOrders" label="门店订单" width="120"></el-table-column>
              <el-table-column prop="totalMemberOrders" label="会员订单" width="120"></el-table-column>
              <el-table-column prop="totalRevenue" label="总收入" width="150">
                <template slot-scope="scope">
                  ¥{{ scope.row.totalRevenue.toFixed(2) }}
                </template>
              </el-table-column>
            </el-table>
          </el-form-item>
        </el-form>

        <!-- 导出按钮 -->
        <div class="action-area">
          <el-button 
            type="primary" 
            size="large" 
            icon="el-icon-download"
            @click="handleBatchExport"
            :loading="exporting"
            :disabled="selectedYears.length === 0">
            {{ exporting ? '导出中...' : `批量导出 (${selectedYears.length}年)` }}
          </el-button>
          <el-button 
            size="large" 
            icon="el-icon-document"
            @click="previewData"
            :disabled="selectedYears.length === 0">
            预览数据
          </el-button>
        </div>

        <!-- 导出进度 -->
        <div v-if="exporting" class="progress-area">
          <el-progress 
            :percentage="progressPercent" 
            :status="progressStatus"
            :format="progressFormat">
          </el-progress>
          <div class="progress-detail">
            <p>正在导出：{{ currentExportInfo }}</p>
            <p>已完成：{{ completedCount }} / {{ totalCount }} 个年份</p>
          </div>
        </div>

        <!-- 导出结果 -->
        <div v-if="exportResults.length > 0" class="results-area">
          <el-divider content-position="left">导出结果</el-divider>
          <el-alert
            title="导出完成！"
            type="success"
            :closable="false"
            show-icon
            style="margin-bottom: 15px;">
          </el-alert>
          <el-table :data="exportResults" border style="width: 100%;">
            <el-table-column prop="year" label="年份" width="100"></el-table-column>
            <el-table-column prop="status" label="状态" width="120">
              <template slot-scope="scope">
                <el-tag :type="scope.row.success ? 'success' : 'danger'">
                  {{ scope.row.success ? '成功' : '失败' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="filename" label="文件名"></el-table-column>
          </el-table>
        </div>

        <!-- 数据预览对话框 -->
        <el-dialog
          title="数据预览"
          :visible.sync="previewVisible"
          width="80%">
          <el-table :data="previewDataList" border max-height="500">
            <el-table-column prop="year" label="年份" width="80"></el-table-column>
            <el-table-column prop="month" label="月份" width="80"></el-table-column>
            <el-table-column prop="totalCourseOrders" label="课程订单" width="120"></el-table-column>
            <el-table-column prop="coachOrders" label="教练订单" width="120"></el-table-column>
            <el-table-column prop="storeOrders" label="门店订单" width="120"></el-table-column>
            <el-table-column prop="memberOrders" label="会员订单" width="120"></el-table-column>
            <el-table-column label="价格分布">
              <template slot-scope="scope">
                <div>9.9元: {{ scope.row.price9_9 }}单</div>
                <div>99元: {{ scope.row.price99 }}单</div>
                <div>199元: {{ scope.row.price199 }}单</div>
                <div>1680元: {{ scope.row.price1680 }}单</div>
                <div>2480元: {{ scope.row.price2480 }}单</div>
              </template>
            </el-table-column>
          </el-table>
          <span slot="footer" class="dialog-footer">
            <el-button @click="previewVisible = false">关 闭</el-button>
          </span>
        </el-dialog>
      </div>
    </el-card>
  </div>
</template>

<script>
import { 
  getAvailableYears, 
  getYearStatistics,
  getMonthDataConfig,
  exportMultipleYearsCsv
} from '@/utils/batchExportCsv';
import { fetchCourseCategories } from '@/utils/generateOrderCsv';

export default {
  name: 'BatchExportCsv',
  data() {
    return {
      exporting: false,
      selectedYears: [],
      availableYears: [],
      yearStats: {},
      exportResults: [],
      progressPercent: 0,
      progressStatus: '',
      currentExportInfo: '',
      completedCount: 0,
      totalCount: 0,
      previewVisible: false,
      previewDataList: [],
      categories: []
    };
  },
  computed: {
    selectedYearStats() {
      return this.selectedYears.map(year => this.yearStats[year]).filter(Boolean);
    }
  },
  async created() {
    this.availableYears = getAvailableYears();
    this.loadYearStats();
    // 预加载课程类目
    this.categories = await fetchCourseCategories();
  },
  methods: {
    loadYearStats() {
      this.availableYears.forEach(year => {
        this.yearStats[year] = getYearStatistics(year);
      });
    },
    
    handleYearChange() {
      // 年份选择变化时的处理
    },
    
    selectAllYears() {
      this.selectedYears = [...this.availableYears];
    },
    
    deselectAllYears() {
      this.selectedYears = [];
    },
    
    selectRecentYears() {
      const sortedYears = [...this.availableYears].sort((a, b) => b - a);
      this.selectedYears = sortedYears.slice(0, 2);
    },
    
    async handleBatchExport() {
      if (this.selectedYears.length === 0) {
        this.$message.warning('请至少选择一个年份');
        return;
      }

      this.exporting = true;
      this.exportResults = [];
      this.progressPercent = 0;
      this.progressStatus = '';
      
      try {
        this.totalCount = this.selectedYears.length;
        this.completedCount = 0;

        // 进度回调函数
        const onProgress = (currentYear, totalYears, year) => {
          this.completedCount = currentYear;
          this.currentExportInfo = `${year}年`;
          this.progressPercent = Math.floor((currentYear / totalYears) * 100);
        };

        // 执行批量导出
        const results = await exportMultipleYearsCsv(
          this.selectedYears,
          this.categories,
          onProgress
        );

        // 处理导出结果
        this.exportResults = results.map(result => ({
          year: result.year,
          success: result.success,
          filename: `order_data_${result.year}.csv`
        }));

        this.progressStatus = 'success';
        const successCount = results.filter(r => r.success).length;
        this.$message.success(`成功导出 ${successCount}/${this.selectedYears.length} 年的数据！`);
      } catch (error) {
        console.error('批量导出失败:', error);
        this.progressStatus = 'exception';
        this.$message.error('批量导出失败，请重试');
      } finally {
        this.exporting = false;
      }
    },
    
    previewData() {
      this.previewDataList = [];
      
      this.selectedYears.forEach(year => {
        const stats = getYearStatistics(year);
        Object.keys(stats.months).forEach(month => {
          const monthData = stats.months[month];
          this.previewDataList.push({
            year,
            month: parseInt(month),
            ...monthData
          });
        });
      });
      
      this.previewVisible = true;
    },
    
    progressFormat(percentage) {
      return percentage === 100 ? '完成' : `${percentage}%`;
    }
  }
};
</script>

<style scoped lang="less">
.batch-export-container {
  padding: 20px;
  
  .box-card {
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .content {
    ul {
      margin: 10px 0;
      padding-left: 20px;
      
      li {
        margin: 5px 0;
        line-height: 1.6;
      }
    }
  }
  
  .config-form {
    margin-bottom: 30px;
    
    .year-stats {
      color: #909399;
      font-size: 12px;
      margin-left: 5px;
    }
  }
  
  .action-area {
    text-align: center;
    margin: 30px 0;
    
    .el-button {
      padding: 15px 40px;
      font-size: 16px;
      margin: 0 10px;
    }
  }
  
  .progress-area {
    margin: 30px 0;
    padding: 20px;
    background-color: #f5f7fa;
    border-radius: 4px;
    
    .progress-detail {
      margin-top: 15px;
      text-align: center;
      
      p {
        margin: 5px 0;
        color: #606266;
      }
    }
  }
  
  .results-area {
    margin-top: 30px;
  }
}
</style>